import {
  Resizable,
  ResizableProvider,
} from 'articleEditor/components/heightResize';
import { ResizeHandle } from 'articleEditor/components/resizeHandle';
import { UseFloatingMenuReturn } from 'articleEditor/hooks/useFloatingMenu';
import { basicBlockActions } from 'articleEditor/utils/actions';
import cx from 'classnames';
import {
  findNodePath,
  useEditableProps,
} from 'lib-knowledge-document-structure/plate/common';
import { useMyEditorRef } from 'lib-knowledge-document-structure/plateHooks';
import {
  MyEditor,
  MyEmbedElement,
} from 'lib-knowledge-document-structure/plateTypes';
import { Path } from 'lib-knowledge-document-structure/slate/package';
import { useSelected } from 'lib-knowledge-document-structure/slate/react';
import { useMemo } from 'react';

import resizeStyles from '../../resizeHandle/resizeHandle.module.scss';
import { SortableBlockWrapper } from '../baseBlock';
import s from '../baseBlock/block.module.scss';
import { EmbedBlockProps } from './embedBlock';

type EmbedBlockValueProps = EmbedBlockProps & {
  floatingLinkMenu: UseFloatingMenuReturn;
};

function EmbedBlockValue({
  element,
  attributes,
  floatingLinkMenu,
  children,
}: EmbedBlockValueProps): JSX.Element {
  const editor = useMyEditorRef();
  const path = findNodePath(editor, element);
  const { readOnly } = useEditableProps();
  const selected = useSelected();
  if (!path) throw new Error('EmbedBlockValue path is not defined'); // this cant happen, path should always be defined

  const blockActions = useMemo(
    () =>
      createBlockActions({
        editor,
        element,
        openLinkMenu: floatingLinkMenu.openFloatingMenu,
        path,
      }),
    [editor, element, floatingLinkMenu, path],
  );

  return (
    <ResizableProvider>
      <SortableBlockWrapper
        className={cx(
          s['blockEmbedValue'],
          resizeStyles['resizeContainer'],
          selected && resizeStyles['resizeContainerHoverFocused'],
        )}
        element={element}
        blockActions={blockActions}
      >
        <div
          {...attributes}
          contentEditable={false}
          className={s['blockContent']}
        >
          <Resizable
            options={{ readOnly: readOnly, maxHeight: '1200px' }}
            className={cx(
              s['blockEmbedPreview'],
              selected && s['focused'],
              readOnly && s['imageReadonly'],
            )}
          >
            <iframe
              ref={floatingLinkMenu.floatingResult.refs.setReference}
              src={element.src!}
              title={element.title}
              allowFullScreen
            />
            {children}
            {!readOnly && <ResizeHandle side="bottom" />}
          </Resizable>
        </div>
      </SortableBlockWrapper>
    </ResizableProvider>
  );
}

function createBlockActions({
  openLinkMenu,
}: {
  editor: MyEditor;
  element: MyEmbedElement;
  openLinkMenu: () => void;
  path: Path;
}) {
  return [
    ...basicBlockActions,
    {
      key: 'replace',
      icon: 'external-link' as const,
      label: 'Edit',
      isSelected: false,
      action: openLinkMenu,
    },
  ] as const;
}

export default EmbedBlockValue;
